import { FormEvent, useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { TIMEZONE_OPTIONS, detectUserTimezone } from '../lib/timezones';
import {
  fetchCurrentProfile,
  upsertCurrentProfile,
  type UserProfile,
} from '../services/profileService';

interface ProfileSetupModalProps {
  isOpen: boolean;
  initialProfile?: UserProfile | null;
  onClose: () => void;
  onProfileSaved?: (profile: UserProfile) => void;
  forceCompletion?: boolean;
}

interface ProfileFormState {
  fullName: string;
  timezone: string;
  persona: string;
  bio: string;
}

const defaultFormState = (): ProfileFormState => ({
  fullName: '',
  timezone: detectUserTimezone(),
  persona: '',
  bio: '',
});

export const ProfileSetupModal: React.FC<ProfileSetupModalProps> = ({
  isOpen,
  initialProfile,
  onClose,
  onProfileSaved,
  forceCompletion = false,
}) => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState<ProfileFormState>(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      fullName: initialProfile?.fullName ?? '',
      timezone: initialProfile?.timezone ?? detectUserTimezone(),
      persona: initialProfile?.persona ?? '',
      bio: initialProfile?.bio ?? '',
    });
    setError(null);
    setSuccess(false);
  }, [initialProfile, isOpen]);

  if (!isOpen) return null;

  const disableClose = forceCompletion && !success;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.fullName.trim()) {
      setError(t.profileMissingName);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await upsertCurrentProfile({
        fullName: form.fullName,
        timezone: form.timezone,
        persona: form.persona || undefined,
        bio: form.bio || undefined,
      });
      const refreshed = await fetchCurrentProfile();
      if (refreshed) {
        onProfileSaved?.(refreshed);
      }
      window.dispatchEvent(
        new CustomEvent<UserProfile | null>('msd-profile-updated', {
          detail: refreshed ?? null,
        }),
      );
      setSuccess(true);
      if (!forceCompletion) {
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 800);
      }
    } catch (err: any) {
      setError(err.message ?? 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (disableClose) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
        {!disableClose && (
          <button
            type="button"
            onClick={closeModal}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
            aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
          >
            <X size={24} />
          </button>
        )}

        <div className="space-y-2 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-500">
            {t.profileSectionTitle}
          </p>
          <h2 className="text-3xl font-bold text-gray-900">{t.profileSetupTitle}</h2>
          <p className="text-sm text-gray-600">{t.profileSetupSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">
              {t.profileFullNameLabel}
            </label>
            <input
              type="text"
              value={form.fullName}
              onChange={(event) => setForm({ ...form, fullName: event.target.value })}
              className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base focus:border-indigo-400 focus:outline-none"
              placeholder={language === 'ar' ? 'اكتب اسمك الكامل' : 'Enter your full name'}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">
                {t.profilePersonaLabel}
              </label>
              <input
                type="text"
                value={form.persona}
                onChange={(event) => setForm({ ...form, persona: event.target.value })}
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base focus:border-indigo-400 focus:outline-none"
                placeholder={language === 'ar' ? 'مثال: قائد الفريق' : 'Optional nickname'}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">
                {t.profileTimezoneLabel}
              </label>
              <select
                value={form.timezone}
                onChange={(event) => setForm({ ...form, timezone: event.target.value })}
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base focus:border-indigo-400 focus:outline-none"
              >
                {TIMEZONE_OPTIONS.map((zone) => (
                  <option key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-gray-500">
              {t.profileBioLabel}
            </label>
            <textarea
              value={form.bio}
              onChange={(event) => setForm({ ...form, bio: event.target.value })}
              className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base focus:border-indigo-400 focus:outline-none"
              rows={3}
            />
          </div>

          {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
          {success && <p className="text-sm font-semibold text-emerald-600">{t.profileSaved}</p>}

          <button
            type="submit"
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-white font-semibold shadow-lg transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
            disabled={saving}
          >
            {saving && <Loader2 className="h-5 w-5 animate-spin" />}
            {t.profileSaveButton}
          </button>
        </form>

        {forceCompletion && !success && (
          <p className="mt-4 text-center text-xs font-semibold uppercase tracking-wide text-amber-600">
            {t.profileMissingName}
          </p>
        )}
      </div>
    </div>
  );
};
